<template>
  <div class="log-viewer">
    <div class="log-viewer-header">
      <h3>Log Entries</h3>
      <div class="log-controls">
        <select v-model="minLogLevel" class="log-level-select" title="Minimum log level">
          <option v-for="item in logLevelItems" :key="item.value" :value="item.value">
            {{ item.text }}
          </option>
        </select>
        <button
          @click="clearLogs"
          class="btn-clear"
          :disabled="!logEntries.length && !executionMeta"
        >
          Clear
        </button>
        <button
          @click="toggleAutoScroll"
          class="btn-auto-scroll"
          :class="{ active: autoScroll }"
        >
          {{ autoScroll ? 'Disable' : 'Enable' }} Auto-scroll
        </button>
      </div>
    </div>

    <div v-if="executionMeta" class="execution-meta" :class="metaStatusClass">
      <div class="meta-row">
        <span class="meta-label">Status</span>
        <span class="meta-value meta-status">{{ displayStatus }}</span>
        <span class="meta-label">Time</span>
        <span class="meta-value">{{ displayResponseTime }}</span>
      </div>
      <div v-if="failureText" class="meta-failure">{{ failureText }}</div>
      <div v-if="metaItems.length" class="meta-details">
        <div v-for="item in metaItems" :key="item.key" class="meta-detail">
          <span class="meta-label">{{ item.key }}</span>
          <span class="meta-value">{{ item.value }}</span>
        </div>
      </div>
    </div>

    <div
      ref="logContainer"
      class="log-container"
      @scroll="handleScroll"
    >
      <div
        v-for="(entry, index) in filteredLogEntries"
        :key="index"
        class="log-entry"
        :class="getLogLevelClass(entry.level)"
        :title="lineTitle(entry)"
      >
        <span class="log-timestamp">{{ formatTimestamp(entry.timestamp) }}</span><span class="log-sep">{{ ' ' }}</span><span class="log-level">{{ displayLevel(entry.level) }}</span><span class="log-sep">: </span><span class="log-message">{{ entry.message }}</span>
      </div>

      <div v-if="!filteredLogEntries.length" class="no-logs">
        No log entries to display
      </div>
    </div>
  </div>
</template>

<script>
var LOG_RANK = {
  trace: 0,
  debug: 1,
  info: 2,
  warn: 3,
  error: 4,
}

export default {
  name: 'LogViewer',
  props: {
    logEntries: {
      type: Array,
      default: () => [],
      validator: (entries) => {
        return !entries || entries.every(entry =>
          entry &&
          typeof entry === 'object' &&
          entry.message != null
        )
      }
    },
    executionMeta: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      autoScroll: true,
      isUserScrolling: false,
      minLogLevel: 'debug',
      logLevelItems: [
        { text: 'Debug', value: 'debug' },
        { text: 'Info', value: 'info' },
        { text: 'Warn', value: 'warn' },
        { text: 'Error', value: 'error' },
      ],
    }
  },
  computed: {
    filteredLogEntries() {
      var min = LOG_RANK[this.minLogLevel] != null ? LOG_RANK[this.minLogLevel] : 0
      return (this.logEntries || []).filter(function (e) {
        var level = (e.level || 'info').toLowerCase()
        var rank = LOG_RANK[level] != null ? LOG_RANK[level] : 1
        return rank >= min
      })
    },
    displayStatus() {
      if (!this.executionMeta) return ''
      var s = this.executionMeta.status || (this.executionMeta.error ? 'failure' : 'success')
      return String(s).toUpperCase()
    },
    displayResponseTime() {
      if (!this.executionMeta || this.executionMeta.responseTime == null) return '—'
      var ms = Number(this.executionMeta.responseTime)
      if (isNaN(ms)) return String(this.executionMeta.responseTime)
      if (ms >= 1000) return (ms / 1000).toFixed(2) + 's'
      return Math.round(ms) + 'ms'
    },
    failureText() {
      if (!this.executionMeta) return ''
      return this.executionMeta.failureMessage || ''
    },
    metaStatusClass() {
      var s = (this.executionMeta && this.executionMeta.status) || ''
      if (this.executionMeta && this.executionMeta.error) return 'is-failure'
      if (String(s).toLowerCase() === 'failure') return 'is-failure'
      if (String(s).toLowerCase() === 'success') return 'is-success'
      if (String(s).toLowerCase() === 'compiling') return 'is-compiling'
      if (String(s).toLowerCase() === 'running') return 'is-running'
      return ''
    },
    metaItems() {
      var meta = this.executionMeta && this.executionMeta.meta
      if (!meta || typeof meta !== 'object') return []
      var items = []
      Object.keys(meta).forEach(function (key) {
        var value = meta[key]
        if (value == null) return
        if (typeof value === 'object') {
          try {
            value = JSON.stringify(value)
          } catch (e) {
            value = String(value)
          }
        }
        items.push({ key: key, value: String(value) })
      })
      return items
    }
  },
  watch: {
    filteredLogEntries: {
      handler() {
        this.$nextTick(() => {
          if (this.autoScroll && !this.isUserScrolling) {
            this.scrollToBottom()
          }
        })
      },
      deep: true
    }
  },
  methods: {
    formatTimestamp(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      return date.toLocaleString()
    },

    lineTitle(entry) {
      var ts = this.formatTimestamp(entry.timestamp)
      var lvl = this.displayLevel(entry.level)
      var msg = entry.message != null ? String(entry.message) : ''
      var head = ts ? (ts + ' ' + lvl) : lvl
      return head ? (head + ': ' + msg) : msg
    },

    displayLevel(level) {
      var key = (level || 'info').toString().toLowerCase()
      return key.toUpperCase()
    },

    getLogLevelClass(level) {
      const levelMap = {
        error: 'level-error',
        warn: 'level-warn',
        info: 'level-info',
        debug: 'level-debug',
        trace: 'level-trace'
      }
      const key = (level || 'info').toLowerCase()
      return levelMap[key] || 'level-info'
    },

    scrollToBottom() {
      const container = this.$refs.logContainer
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },

    handleScroll() {
      const container = this.$refs.logContainer
      if (!container) return

      const { scrollTop, scrollHeight, clientHeight } = container
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10

      this.isUserScrolling = !isAtBottom
    },

    toggleAutoScroll() {
      this.autoScroll = !this.autoScroll
      if (this.autoScroll) {
        this.scrollToBottom()
      }
    },

    clearLogs() {
      this.$emit('clear-logs')
    }
  },

  mounted() {
    this.scrollToBottom()
  }
}
</script>

<style scoped>
.log-viewer {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #1e1e1e;
  border-radius: 8px;
  overflow: hidden;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
}

.log-viewer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #2d2d2d;
  border-bottom: 1px solid #404040;
  gap: 12px;
}

.log-viewer-header h3 {
  margin: 0;
  color: #e0e0e0;
  font-size: 16px;
  font-weight: 600;
  flex-shrink: 0;
}

.log-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.log-level-select {
  padding: 6px 8px;
  border: 1px solid #404040;
  border-radius: 4px;
  background: #3a3a3a;
  color: #e0e0e0;
  font-size: 12px;
  cursor: pointer;
  height: 30px;
}

.log-level-select:hover {
  background: #4a4a4a;
  border-color: #505050;
}

.btn-clear,
.btn-auto-scroll {
  padding: 6px 12px;
  border: 1px solid #404040;
  border-radius: 4px;
  background: #3a3a3a;
  color: #e0e0e0;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;
  height: 30px;
}

.btn-clear:hover,
.btn-auto-scroll:hover {
  background: #4a4a4a;
  border-color: #505050;
}

.btn-clear:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-auto-scroll.active {
  background: #007acc;
  border-color: #007acc;
  color: white;
}

.execution-meta {
  flex: 0 0 auto;
  padding: 10px 14px;
  background: #252525;
  border-bottom: 1px solid #404040;
  font-size: 12px;
}

.execution-meta.is-success {
  border-left: 3px solid #388e3c;
}

.execution-meta.is-failure {
  border-left: 3px solid #d32f2f;
}

.execution-meta.is-compiling {
  border-left: 3px solid #ef6c00;
}

.execution-meta.is-running {
  border-left: 3px solid #1565c0;
}

.meta-row,
.meta-details {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  align-items: baseline;
}

.meta-details {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #333;
}

.meta-detail {
  display: flex;
  gap: 6px;
  align-items: baseline;
  max-width: 100%;
}

.meta-label {
  color: #888;
  text-transform: uppercase;
  font-size: 10px;
  letter-spacing: 0.4px;
}

.meta-value {
  color: #e0e0e0;
  word-break: break-word;
}

.meta-status {
  font-weight: 700;
  letter-spacing: 0.3px;
}

.execution-meta.is-success .meta-status {
  color: #81c784;
}

.execution-meta.is-failure .meta-status {
  color: #e57373;
}

.execution-meta.is-compiling .meta-status {
  color: #ffb74d;
  animation: status-pulse 1.2s ease-in-out infinite;
}

.execution-meta.is-running .meta-status {
  color: #64b5f6;
  animation: status-pulse 1.2s ease-in-out infinite;
}

@keyframes status-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
}

.meta-failure {
  margin-top: 8px;
  color: #ef9a9a;
  white-space: pre-wrap;
  word-break: break-word;
}

.log-container {
  flex: 1;
  min-height: 0;
  overflow: scroll;
  padding: 4px 0;
  background: #1e1e1e;
}

.log-entry {
  display: block;
  padding: 2px 12px;
  border-left: 3px solid #404040;
  color: #e0e0e0;
  white-space: pre-wrap;
  word-break: break-word;
  overflow: visible;
  line-height: 1.45;
  width: max-content;
  min-width: 100%;
  box-sizing: border-box;
}

.log-entry:hover {
  background: #2a2a2a;
}

.log-timestamp {
  color: #c8c8c8;
}

.log-level {
  display: inline-block;
  min-width: 3.6em;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.log-sep {
  color: #c8c8c8;
}

.log-message {
  color: #e0e0e0;
}

.level-error {
  border-left-color: #d32f2f;
}

.level-error .log-message {
  color: #ef9a9a;
}

.level-warn {
  border-left-color: #f57c00;
}

.level-warn .log-message {
  color: #ffcc80;
}

.level-info {
  border-left-color: #1976d2;
}

.level-debug {
  border-left-color: #388e3c;
}

.level-debug .log-message {
  color: #a5d6a7;
}

.level-trace {
  border-left-color: #7b1fa2;
}

.level-trace .log-message {
  color: #ce93d8;
}

.no-logs {
  text-align: center;
  color: #888888;
  padding: 40px 20px;
  font-style: italic;
}

.log-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.log-container::-webkit-scrollbar-track {
  background: #1e1e1e;
}

.log-container::-webkit-scrollbar-thumb {
  background: #404040;
  border-radius: 4px;
}

.log-container::-webkit-scrollbar-thumb:hover {
  background: #505050;
}
</style>
